import { useEffect, useState } from 'react';
import { FieldValues, FormState, Path, UseFormSetFocus, useFormContext } from 'react-hook-form';

type UseFormStatusProps<T extends FieldValues> = {
  /** FormState 값 */
  formState?: FormState<T>;
  /** SetFocus 값 */
  setFocus?: UseFormSetFocus<FieldValues>;
};

/**
 * @description react-hook-form의 폼 유효성에 따른 상태값, 함수를 제공하는 훅
 * @returns
 * - isSubmittable: 폼 제출 가능 여부
 * - enterKeyHint: 가상 키보드 > 엔터 키 힌트
 * - handleEnterKey: 가상 키보드 > 엔터 키 이벤트 처리 함수
 * - blurActiveElement: 포커스 된 요소 블러 처리 함수
 */
export const useFormSubmitStatus = <T extends FieldValues>({ formState, setFocus }: UseFormStatusProps<T> = {}) => {
  const context = useFormContext();

  const { isValid, errors, setFocusValue } = context
    ? {
        isValid: context.formState?.isValid,
        errors: context.formState?.errors,
        setFocusValue: context.setFocus,
      }
    : {
        isValid: formState?.isValid,
        errors: formState?.errors,
        setFocusValue: setFocus,
      };

  const isSubmittable = isValid && (!errors || Object.keys(errors).length === 0);

  const [enterKeyHint, setEnterKeyHint] = useState<'send' | 'next'>('next');

  useEffect(() => {
    setEnterKeyHint(isSubmittable ? 'send' : 'next');
  }, [isSubmittable]);

  const focusToActiveElement = () => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.focus({ preventScroll: true });

      /** 디바이스 키보드 오픈 후, 높이 조정을 위해 딜레이 처리 */
      setTimeout(() => {
        activeElement.scrollIntoView({ behavior: 'smooth' as ScrollBehavior, block: 'center' });
      }, 200);
    }
  };

  const blurActiveElement = () => {
    return new Promise<boolean>((resolve) => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }

      const isKeyboardClosed = () => {
        const buffer = 10;
        if (window.innerHeight <= screen.height + buffer) {
          resolve(true);
        } else {
          requestAnimationFrame(isKeyboardClosed);
        }
      };

      requestAnimationFrame(isKeyboardClosed);
    });
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>, nextField: string) => {
    if (e.key === 'Enter') {
      if (isSubmittable) {
        setTimeout(() => {
          blurActiveElement();
        }, 50);
      } else {
        e.preventDefault();
        setFocusValue?.(nextField as Path<T>);
      }
    }
  };

  return { isSubmittable, enterKeyHint, blurActiveElement, handleEnterKey, focusToActiveElement };
};
